import { Button, Tag } from "@design";
import { PauseCircle, RotateCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { SrcSchemasChatMessageResponse } from "@/api";
import { Text } from "@/components/Text";
import { type PausedReply, pauseOf } from "./chatStreamReducer";
import type { SendInput } from "./useSendMessage";

export { pauseOf } from "./chatStreamReducer";

/** What the model is sent when the person clicks Continue (as frontend/ sends it). */
export const CONTINUE_PROMPT = "Continue from where you left off.";

type StartTurn = (input: Omit<SendInput, "sessionUuid">, onFailed: () => void) => void;

/**
 * "Paused after N steps" → Continue: sends the continuation turn and the backend resumes the work (no files ride
 * along). Only the chat's owner can continue, so others get no handler.
 */
export function continueHandler(
  isOwner: boolean,
  start: StartTurn,
  send: Pick<SendInput, "agentUuid" | "modelId" | "reasoningEffort">,
): (() => void) | undefined {
  if (!isOwner) return undefined;
  return () => start({ ...send, content: CONTINUE_PROMPT, continuation: true }, () => undefined);
}

/** The newest message, if it's a reply that paused: only that one offers Continue. */
export function pausedLastReply(messages: readonly SrcSchemasChatMessageResponse[]): PausedReply | null {
  const last = messages.at(-1);
  return last?.role === "assistant" ? pauseOf(last.messageMetadata) : null;
}

/** A Continue turn the person sent: shown as a small chip, not a message (MHS-860). */
export function isContinuation(message: SrcSchemasChatMessageResponse): boolean {
  return message.role === "user" && message.messageMetadata?.continuation === true;
}

export function ContinuedChip() {
  const { t } = useTranslation("chat", { keyPrefix: "paused" });
  return (
    <div className="flex justify-end">
      <Tag icon={<RotateCw size={12} aria-hidden />}>{t("continued")}</Tag>
    </div>
  );
}

/**
 * Under a reply that stopped at the step limit (or while the AI service was at capacity): says
 * there's more to do, with Continue. Without ``onContinue``, just the note.
 */
export function PausedNotice({
  pause,
  onContinue,
  disabled = false,
}: Readonly<{ pause: PausedReply; onContinue?: () => void; disabled?: boolean }>) {
  const { t } = useTranslation("chat", { keyPrefix: "paused" });
  let title = t("stepsUnknown");
  if (pause.reason === "rate_limited") title = t("rateLimited");
  else if (pause.steps) title = t("steps", { count: pause.steps });
  return (
    <section
      aria-label={t("label")}
      className="ml-10 flex flex-col gap-3 rounded-lg border border-border-subtle bg-surface-secondary px-4 py-3 md:flex-row md:items-center md:justify-between"
    >
      <div className="flex items-start gap-2">
        <PauseCircle size={18} aria-hidden className="mt-0.5 shrink-0 text-content-secondary" />
        <div className="flex flex-col">
          <Text variant="body-sm">{title}</Text>
          <Text variant="caption" tone="secondary">
            {pause.reason === "rate_limited" ? t("rateLimitedHint") : t("hint")}
          </Text>
        </div>
      </div>
      {onContinue && (
        <Button variant="primary" size="sm" onClick={onContinue} disabled={disabled}>
          {t("continue")}
        </Button>
      )}
    </section>
  );
}
